/**
 * Holds a navigation away from a settings page with unsaved edits (HS2-HV4R7C)
 * until the viewer chooses in the unsaved-changes dialog: keep editing, discard
 * the edits (the form returns to its saved values) and continue, or save and
 * continue once the save succeeds. The click guard, Back/Forward, and the
 * dialog's actions all go through here.
 */
import {
  changedSettingsFields,
  type ExplicitSavePage,
  explicitSavePage,
  readSettingsForm,
  resetSettingsForm,
  settingsFormBaseline,
} from '../lib/unsaved-settings.js';
import { saveAssumptions, saveProfile, saveThresholds } from './loaders.js';
import { settings, settingsPage, settingsTab, unsavedChangesPrompt, view } from './state.js';

interface HeldNavigation {
  proceed: () => void;
  /** Undo what the browser already did (Back/Forward moved the URL) when the viewer stays. */
  stay?: () => void;
}

let held: HeldNavigation | null = null;
let continuing = false;

const SAVERS: Record<ExplicitSavePage, (root: HTMLElement) => Promise<boolean>> = {
  profile: saveProfile,
  'verdict-thresholds': saveThresholds,
  'landed-cost': saveAssumptions,
};

function currentPage(): ExplicitSavePage | null {
  return explicitSavePage(view.value, settingsTab.value, settingsPage.value);
}

/** The explicit-save page being edited when its form differs from the saved settings. */
export function dirtySettingsPage(root: HTMLElement): ExplicitSavePage | null {
  const page = currentPage();
  const saved = settings.value;
  if (page === null || saved === null) return null;
  const changed = changedSettingsFields(page, settingsFormBaseline(page, saved), readSettingsForm(root, page));
  return changed.length > 0 ? page : null;
}

/**
 * Hold `proceed` when the page has unsaved edits and ask the viewer; returns
 * whether it was held. A navigation the dialog itself continues is never held again.
 */
export function holdNavigation(root: HTMLElement, navigation: HeldNavigation): boolean {
  if (continuing) return false;
  const page = dirtySettingsPage(root);
  if (page === null) return false;
  held = navigation;
  unsavedChangesPrompt.value = { page, saving: false };
  return true;
}

function continueHeld(): void {
  const navigation = held;
  held = null;
  unsavedChangesPrompt.value = null;
  continuing = true;
  try {
    navigation?.proceed();
  } finally {
    continuing = false;
  }
}

/** Keep editing (also Escape and an outside press): drop the held navigation. */
export function keepEditing(): void {
  if (unsavedChangesPrompt.value?.saving === true) return;
  const navigation = held;
  held = null;
  unsavedChangesPrompt.value = null;
  navigation?.stay?.();
}

/** Discard changes: put the saved values back, then continue. */
export function discardAndContinue(root: HTMLElement): void {
  const prompt = unsavedChangesPrompt.value;
  if (prompt === null || prompt.saving) return;
  if (settings.value !== null) resetSettingsForm(root, prompt.page, settingsFormBaseline(prompt.page, settings.value));
  continueHeld();
}

/** Save: continue only when the save succeeds; a failure stays on the page with its message. */
export async function saveAndContinue(root: HTMLElement): Promise<void> {
  const prompt = unsavedChangesPrompt.value;
  if (prompt === null || prompt.saving) return;
  unsavedChangesPrompt.value = { ...prompt, saving: true };
  const saved = await SAVERS[prompt.page](root);
  if (!saved) {
    const navigation = held;
    held = null;
    unsavedChangesPrompt.value = null;
    navigation?.stay?.();
    return;
  }
  // Show the values as the server stored them before leaving.
  if (settings.value !== null) resetSettingsForm(root, prompt.page, settingsFormBaseline(prompt.page, settings.value));
  continueHeld();
}
